import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, ShoppingBag, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { newIdempotencyKey } from "@/lib/idempotency";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { useUIStore } from "@/store/ui-store";
import type { Product } from "@/types/catalog";
import type { Order, PlaceOrderPayload } from "@/types/order";

export function CartDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { cart, updateCart, removeFromCart, clearCart } = useUIStore();
  const [placing, setPlacing] = useState(false);
  const nav = useNavigate();

  const variantIds = Object.keys(cart).map(Number);

  const { data: products } = useQuery({
    queryKey: ["cart-products", variantIds],
    queryFn: () => get<Product[]>("/catalog/products"),
    enabled: variantIds.length > 0,
  });

  const lines = useMemo(() => {
    if (!products) return [];
    const map = new Map<number, { product: Product; variantId: number; price: number }>();
    for (const p of products) {
      for (const v of p.variants) {
        map.set(v.id, { product: p, variantId: v.id, price: Number(v.price) });
      }
    }
    return variantIds
      .map((id) => {
        const info = map.get(id);
        if (!info) return null;
        const qty = cart[id];
        return {
          ...info,
          quantity: qty,
          lineTotal: info.price * qty,
        };
      })
      .filter(Boolean) as {
        product: Product; variantId: number; price: number;
        quantity: number; lineTotal: number;
      }[];
  }, [products, variantIds, cart]);

  const total = lines.reduce((sum, l) => sum + l.lineTotal, 0);

  const placeOrder = async () => {
    if (lines.length === 0) return;
    setPlacing(true);
    try {
      const payload: PlaceOrderPayload = {
        items: lines.map((l) => ({ variant_id: l.variantId, quantity: l.quantity })),
      };
      const order = await post<Order>("/orders/orders", payload, {
        idempotencyKey: newIdempotencyKey(),
      });
      toast.success(`Order ${order.order_id} placed successfully`);
      clearCart();
      onClose();
      nav(`/orders/${order.order_id}/pay`);
    } catch { /* interceptor */ }
    finally { setPlacing(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-surface-900/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 350, damping: 32 }}
            className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-elevate-3 flex flex-col"
          >
            <div className="h-16 px-5 border-b border-surface-200 flex items-center justify-between">
              <div>
                <div className="text-base font-semibold text-surface-900">Your Cart</div>
                <div className="text-xs text-surface-500">{lines.length} item(s)</div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-surface-100 rounded-md">
                <X className="h-5 w-5 text-surface-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {lines.length === 0 ? (
                <EmptyState icon={ShoppingBag} title="Your cart is empty"
                             description="Browse the catalog to add products." />
              ) : (
                <ul className="space-y-3">
                  {lines.map((l) => (
                    <li key={l.variantId} className="border border-surface-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-surface-900 truncate">
                            {l.product.name}
                          </div>
                          <div className="text-2xs text-surface-500 font-mono">
                            SKU: {l.product.variants.find((v) => v.id === l.variantId)?.sku}
                          </div>
                          <div className="text-sm text-surface-700 mt-1">{formatCurrency(l.price)} each</div>
                        </div>
                        <button onClick={() => removeFromCart(l.variantId)}
                                className="text-surface-400 hover:text-danger-600 p-1"
                                aria-label="Remove">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-1 border border-surface-300 rounded-md">
                          <button onClick={() => updateCart(l.variantId, l.quantity - 1)}
                                  className="h-8 w-8 flex items-center justify-center hover:bg-surface-50">
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <div className="w-8 text-center text-sm font-semibold">{l.quantity}</div>
                          <button onClick={() => updateCart(l.variantId, l.quantity + 1)}
                                  className="h-8 w-8 flex items-center justify-center hover:bg-surface-50">
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="text-sm font-bold text-surface-900">{formatCurrency(l.lineTotal)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {lines.length > 0 && (
              <div className="border-t border-surface-200 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-surface-600">Subtotal</div>
                  <div className="text-xl font-bold text-surface-900">{formatCurrency(total)}</div>
                </div>
                <Button size="lg" fullWidth loading={placing} onClick={placeOrder}>
                  Place order
                </Button>
                <div className="text-2xs text-center text-surface-500">
                  You'll submit payment details after placing the order.
                </div>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
